import { useHandleClickMarketPrice } from '@src/components/MarketDataCard/useHandleClickMarketPrice';
import { useGetOrderCurrency } from '@src/components/OMS/useGetOrderCurrency';
import { OrderBook } from '@src/components/OrderBook/OrderBook';
import { useDeepDiveContext } from '@src/providers/DeepDiveProvider';
import { useDisplaySettings } from '@src/providers/DisplaySettingsProvider';
import {
  MaxOrderSizeDialogs,
  MixpanelEvent,
  MixpanelEventSource,
  UnifiedLiquidityEnum,
  useMixpanel,
  type OrderIntent,
  type SideEnum,
} from '@talos/kyoko';
import { useCallback } from 'react';

export function MobileOrderBookModule() {
  const mixpanel = useMixpanel();
  const {
    security,
    selectedMarkets,
    selectedMarketAccounts,
    selectedAggregation,
    priceIncrement,
    showCumQty,
    setShowCumQty,
    setPriceIncrement,
  } = useDeepDiveContext();
  const { showAllInPrices, showFirmLiquidity } = useDisplaySettings();
  const { getOrderCurrency } = useGetOrderCurrency();

  const {
    clickToTradePreviewOrder,
    onClickPrice: handleClickPrice,
    onCancelActive: handleCancelActive,
    maxOrderSizeDialogs,
  } = useHandleClickMarketPrice({
    symbol: security?.Symbol,
    marketAccounts: selectedMarketAccounts,
    markets: selectedMarkets,
    aggregation: selectedAggregation,
    unifiedLiquidity: UnifiedLiquidityEnum.Disabled,
    defaultSubAccountId: undefined,
    subAccount: undefined,
    orderPresetID: undefined,
  });

  const handleCumQtyClick = useCallback(
    (nextShowCumQty: boolean) => {
      mixpanel.track(MixpanelEvent.ChangeQuantityToggle);
      setShowCumQty(nextShowCumQty);
    },
    [mixpanel, setShowCumQty]
  );

  const handleRowClick = useCallback(
    ({ price, side, quantity, intent }: { price: string; side: SideEnum; quantity: string; intent: OrderIntent }) => {
      if (!security) {
        return;
      }
      handleClickPrice({
        values: {
          price,
          currency: getOrderCurrency(security, side),
          side,
          orderQty: quantity,
          intent,
        },
        source: MixpanelEventSource.OrderBook,
        activateOrder: true,
      });
    },
    [handleClickPrice, security, getOrderCurrency]
  );

  if (!security) {
    return null;
  }

  return (
    <>
      <OrderBook
        security={security}
        priceIncrement={priceIncrement ?? security.DefaultPriceIncrement}
        selectedMarkets={selectedMarkets}
        selectedMarketAccounts={selectedMarketAccounts}
        onRowClick={handleRowClick}
        onMouseLeaveRow={handleCancelActive}
        onCumQtyClick={handleCumQtyClick}
        showCumQty={showCumQty}
        showAllInPrices={showAllInPrices}
        showFirmLiquidity={showFirmLiquidity}
        clickToTradePreviewOrder={clickToTradePreviewOrder}
        onIncrementChange={(_index, increment) => setPriceIncrement(increment)}
        showPriceIncrementSetting
      />
      <MaxOrderSizeDialogs {...maxOrderSizeDialogs} />
    </>
  );
}
